import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { ContactCycleRuleApiService } from '../services/contact-cycle-rule-api.service';
import { ContactCycleRuleResponse } from '../models/contact-cycle-rule.model';
import { ContactCycleApiService } from '../../contact-cycles/services/contact-cycle-api.service';
import { ContactCycleResponse } from '../../contact-cycles/models/contact-cycle.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { ToastService } from '../../../shared/services/toast.service';

type BadgeColor = 'primary' | 'success' | 'error' | 'warning' | 'info' | 'light' | 'dark';

@Component({
  selector: 'app-contact-cycle-rule-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    BadgeComponent,
    HasPermissionDirective
  ],
  templateUrl: './contact-cycle-rule-list.component.html'
})
export class ContactCycleRuleListComponent implements OnInit {
  reglas: ContactCycleRuleResponse[] = [];
  ciclos: ContactCycleResponse[] = [];
  loading = false;
  error = '';

  // Filtro por ciclo (client-side)
  filtroCicloId: number | undefined = undefined;

  constructor(
    private ruleApi: ContactCycleRuleApiService,
    private cycleApi: ContactCycleApiService,
    private authService: AuthService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
  ) { }

  ngOnInit(): void {
    this.cargarDatos();
  }

  cargarDatos(): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    // Load cycles and rules in parallel
    this.cycleApi.getCycles(businessId).subscribe({
      next: (ciclos) => {
        this.ciclos = ciclos;
      },
      error: (err) => {
        console.error('Error cargando ciclos:', err);
      }
    });

    this.ruleApi.getRules(businessId).subscribe({
      next: (data) => {
        this.reglas = data;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar las reglas de ciclo';
        this.loading = false;
        this.toast.error({ title: 'Error', message: this.error });
        console.error('Error:', err);
      }
    });
  }

  get reglasFiltradas(): ContactCycleRuleResponse[] {
    if (this.filtroCicloId === undefined || this.filtroCicloId === null) {
      return this.reglas;
    }
    return this.reglas.filter(r => r.idContactCycle === +this.filtroCicloId!);
  }

  nuevaRegla(): void {
    this.router.navigate(['/config-contactos/reglas/crear']);
  }

  editarRegla(regla: ContactCycleRuleResponse): void {
    this.router.navigate(['/config-contactos/reglas/editar', regla.id]);
  }

  async eliminarRegla(regla: ContactCycleRuleResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar regla',
      message: regla.cycleName
        ? `¿Está seguro de eliminar esta regla del ciclo "${regla.cycleName}"?`
        : '¿Está seguro de eliminar esta regla de ciclo?'
    });
    if (!ok) return;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.ruleApi.deleteRule(regla.id, businessId).subscribe({
      next: () => {
        this.toast.deleted(
          'Regla de ciclo',
          regla.cycleName
            ? `Se eliminó la regla del ciclo "${regla.cycleName}" correctamente.`
            : 'Se eliminó la regla de ciclo correctamente.'
        );
        this.cargarDatos();
      },
      error: (err) => {
        const msg = err.error?.message || 'Error al eliminar la regla';
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  getOperationColor(operationType: string): BadgeColor {
    if (operationType === 'SET') return 'primary';
    if (operationType === 'INCREMENT') return 'warning';
    return 'info';
  }

  getOperationLabel(operationType: string): string {
    if (operationType === 'SET') return 'SET';
    if (operationType === 'INCREMENT') return 'INCREMENT';
    return 'COPY_FROM_FIELD';
  }

  getActiveColor(isActive: boolean): BadgeColor {
    return isActive ? 'success' : 'error';
  }

  getActiveLabel(isActive: boolean): string {
    return isActive ? 'Activo' : 'Inactivo';
  }
}
